// src/components/admin/AdminSidebar.tsx
// ============================================================
// SALT — Admin Sidebar
//
// Navigation sidebar for the admin panel. Shows links based on
// user role (admin sees all, moderator sees Dashboard + Disputes).
//
// UPDATED: Added Reports link for admin users.
// ============================================================

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { Notification } from '../../types/notification.types';

interface AdminSidebarProps {
  userRole: 'admin' | 'moderator';
  notifications: Notification[];
}

interface SidebarLink {
  label: string;
  to: string;
  icon: string;
  adminOnly: boolean;
  badgeType?: string;
}

const SIDEBAR_LINKS: SidebarLink[] = [
  { label: 'Dashboard', to: '/admin', icon: '📊', adminOnly: false },
  { label: 'Disputes', to: '/admin/disputes', icon: '⚠️', adminOnly: false, badgeType: 'admin_dispute_opened' },
  { label: 'Reports', to: '/admin/reports', icon: '🚩', adminOnly: true },
  { label: 'Users', to: '/admin/users', icon: '👥', adminOnly: true },
  { label: 'News', to: '/admin/news', icon: '📰', adminOnly: true },
  { label: 'Audit Log', to: '/admin/audit-log', icon: '📋', adminOnly: true },
];

export default function AdminSidebar({ userRole, notifications }: AdminSidebarProps) {
  const navigate = useNavigate();
  const isAdmin = userRole === 'admin';

  const disputeBadgeCount = notifications.filter(
    (n) => (n.type as string) === 'admin_dispute_opened' && !n.isRead
  ).length;

  const visibleLinks = SIDEBAR_LINKS.filter(
    (link) => !link.adminOnly || isAdmin
  );

  const handleBackToSite = () => {
    navigate('/');
  };

  return (
    <aside className="sl-admin-sidebar">
      <div className="sl-admin-sidebar-header">
        <div className="sl-admin-sidebar-logo">
          <span className="sl-admin-sidebar-logo-icon">⛰</span>
          <div className="sl-admin-sidebar-logo-text">
            <span className="sl-admin-sidebar-title">SALT</span>
            <span className="sl-admin-sidebar-subtitle">Admin Panel</span>
          </div>
        </div>
      </div>

      <nav className="sl-admin-sidebar-nav">
        {visibleLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/admin'}
            className={({ isActive }) =>
              `sl-admin-sidebar-link ${isActive ? 'sl-admin-sidebar-link--active' : ''}`
            }
          >
            <span className="sl-admin-sidebar-link-icon">{link.icon}</span>
            <span className="sl-admin-sidebar-link-label">{link.label}</span>
            {link.badgeType && disputeBadgeCount > 0 && (
              <span className="sl-admin-sidebar-badge">{disputeBadgeCount}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="sl-admin-sidebar-footer">
        <button
          type="button"
          className="sl-admin-sidebar-link sl-admin-sidebar-back"
          onClick={handleBackToSite}
        >
          <span className="sl-admin-sidebar-link-icon">←</span>
          <span className="sl-admin-sidebar-link-label">Back to Site</span>
        </button>
      </div>
    </aside>
  );
}